import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { line, Size, cents, computeLineAmount, lineDiscountOf, t, type SuggestionRow, documentName, newDocumentTitle } from '@bms2/shared';
import { Checkbox, DatePicker, Dropdown, NumericInput, TextInput, Textarea } from '../../components/controls';
import { ModalField } from '../../components/modals/ModalShell';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { CREDIT_NOTES_PATH, INVOICES_PATH, apiFetch, post, useApi } from './api';
import { usePersonSearch } from '../settings/api';
import lineStyles from './DocumentPage.module.css';
import { Money } from '../../components/Money';
import { DocumentTotals } from '../../components/composites/DocumentTotals';
import { useTaxPreview } from './useTaxPreview';
import { useAddNew } from './AddNewHost';
import { useDocumentToday } from '../../shell/ViewerZone';

/** What the form opens on: the invoice it credits and that invoice's own lines with what each has had credited. */
export interface CreditNoteDefaults {
  sourceInvoiceId: string;
  sourceInvoiceNumber: string | null;
  companyId: string;
  companyName: string;
  currencyCode: string;
  creditDate: string;
  lines: { sourceLineId: string; description: string; quantity: number; alreadyCredited: number; unitPriceCents: number | null; returnable: boolean; discountType: string | null; discountValue: number | null }[];
}

/** 🔨 What the line still has to give, which is what the box is bounded at; the server refuses anything past it, and refusing at the save is refusing too late. */
function remainderOf(row: CreditNoteDefaults['lines'][number]): number {
  return Math.max(0, row.quantity - row.alreadyCredited);
}

/** What a line says beneath its own name: what is being credited of what was billed, what is left to credit, and whether it comes back. */
function lineNote(row: CreditNoteDefaults['lines'][number], credited: number): string {
  const remaining = remainderOf(row);
  return line(
    `${credited} ${t('creditNote.lines.ofQuantity')} ${row.quantity}`,
    row.alreadyCredited > 0 ? `· ${row.alreadyCredited} ${t('creditNote.lines.alreadyCredited')}` : null,
    remaining <= 0 ? `· ${t('creditNote.lines.fullyCredited')}` : `· ${remaining} ${t('creditNote.lines.remaining')}`,
    row.returnable ? null : `· ${t('creditNote.lines.notReturnable')}`,
  );
}

/**
 * 🔒 The Credit Note's edit mode (decision 0409), on the note's own page. The
 * header takes the invoice it credits, the reason, the credit date and whether
 * the goods come back, with the bypass beneath where they do not; the line
 * block is the invoice's own lines with a quantity to credit against each.
 *
 * 🔨 The line block is not the Item Table: a credit note credits a line that
 * already exists and never names a new item, so a picker on it would offer
 * something the server refuses.
 *
 * 🔨 The footer carries one control and Cancel, and no Save as Draft. A credit
 * note is issued the moment it is raised, so a draft would be a state the
 * record has no column for and a second control doing the one act.
 */
export function CreditNoteForm({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invoiceFromUrl = params.get('invoiceId');
  const [invoice, setInvoice] = useState<{ id: string; label: string } | null>(null);
  const invoiceId = invoiceFromUrl || invoice?.id || '';
  const defaults = useApi<CreditNoteDefaults>(invoiceId ? `${CREDIT_NOTES_PATH}/defaults?invoiceId=${encodeURIComponent(invoiceId)}` : null, apiBaseUrl);
  const answer = defaults.state.kind === 'ready' ? defaults.state.data : null;
  const readFailed = defaults.state.kind === 'failed';

  const [reason, setReason] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [creditDate, setCreditDate] = useState(today);
  const [restocks, setRestocks] = useState(true);
  const [bypassReason, setBypassReason] = useState('');
  const [authoriser, setAuthoriser] = useState<{ id: string; label: string } | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [seen, setSeen] = useState<CreditNoteDefaults | null>(null);
  if (answer && seen !== answer) {
    setSeen(answer);
    setCreditDate(today);
  }

  // 🔒 The tax each credited line carries back is worked out live, as its share of the invoice line's, through the call the save makes (decision 0644).
  const preview = useTaxPreview(
    answer
      ? {
          documentType: 'CreditNote', invoiceId: answer.sourceInvoiceId,
          lines: Object.entries(quantities).filter(([, quantity]) => quantity > 0).map(([sourceLineId, quantity]) => ({ key: sourceLineId, sourceLineId, quantity })),
        }
      : null,
    apiBaseUrl,
  );

  const invoiceSearch = async (term: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    // 🔒 Issued invoices alone: a Draft is owed by nobody and a void one is gone, so neither is offered (T921; decision 1704).
    const response = await apiFetch(`${apiBaseUrl}${INVOICES_PATH}?q=${encodeURIComponent(term)}&creditable=true`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; number: string; companyName: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.number, line2: row.companyName }));
  };
  const personSearch = usePersonSearch(apiBaseUrl);

  /** What the credited quantities come to before tax, drawn until the first preview lands. */
  const creditedNetCents = (answer?.lines ?? []).reduce((sum, row) => {
    const discount = lineDiscountOf(row.discountType, row.discountValue, answer?.currencyCode ?? 'USD');
    return sum + computeLineAmount(quantities[row.sourceLineId] ?? 0, cents(row.unitPriceCents ?? 0), discount ?? undefined).amount;
  }, 0);

  const save = async (): Promise<void> => {
    if (!answer) return setError(t('creditNote.new.chooseInvoice'));
    const lines = Object.entries(quantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([sourceLineId, quantity]) => ({ sourceLineId, quantity }));
    if (lines.length === 0) return setError(t('creditNote.new.needsLines'));
    setBusy(true);
    const body = {
      sourceInvoiceId: answer.sourceInvoiceId,
      reason: reason.trim(),
      returnsToInventory: restocks,
      creditDate: creditDate || null,
      customerNotes,
      lines,
      ...(restocks ? {} : { bypassReason: bypassReason.trim() || null, bypassedByPersonId: authoriser?.id ?? null }),
    };
    const saved = await post<{ id: string }>(CREDIT_NOTES_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    navigate(`/credit-notes/${saved.data.id}`);
  };

  return (
    <>
    <DocumentEditPage
      title={newDocumentTitle('CreditNote')}
      subtitle={answer?.companyName ?? documentName('CreditNote')}
      outwardLabel={t('creditNote.new.raise')}
      busy={busy}
      error={error ?? (readFailed ? t('creditNote.new.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveAndSend={() => void save()}
      onCancel={() => navigate('/credit-notes')}
      header={
        <>
          {invoiceFromUrl ? (
            <ModalField label={t('creditNote.new.invoice')}>
              <span data-credit-note-invoice={answer?.sourceInvoiceId ?? ''}>{answer?.sourceInvoiceNumber ?? ''}</span>
            </ModalField>
          ) : (
            <div data-credit-note-field="invoice" data-credit-note-invoice={answer?.sourceInvoiceId ?? ''}>
              {/* 🔒 No Add New row: a new invoice is a Draft, which is never credited (decision 1704). */}
              <Dropdown size={Size.Medium} label={t('creditNote.new.invoice')} entityLabel={t('creditNote.new.invoiceEntity')} search={invoiceSearch} value={invoice?.label ?? null} onSelect={(row) => setInvoice({ id: row.id, label: row.line1 })} onAddNew={null} required />
            </div>
          )}
          <TextInput size={Size.Large} label={t('creditNote.new.reason')} value={reason} onChange={setReason} required name="creditNoteReason" />
          <DatePicker size={Size.Small} label={t('creditNote.new.creditDate')} value={creditDate} onChange={setCreditDate} mode="date" name="creditDate" />
          <Checkbox label={t('creditNote.new.returnsToInventory')} checked={restocks} onChange={setRestocks} size={Size.Medium} />
          {/* 🔨 A bypass credits without the goods coming back, so it stands only where the goods do not return (piece 22). */}
          {!restocks ? (
            <div className={styles.wide} data-credit-note-bypass="true">
              <ModalField label={t('creditNote.new.bypassReason')}>
                <Textarea size={Size.Large} label={t('creditNote.new.bypassReason')} labelHidden value={bypassReason} onChange={setBypassReason} name="bypassReason" />
              </ModalField>
              <Dropdown size={Size.Medium} label={t('creditNote.new.bypassAuthoriser')} entityLabel={t('creditNote.new.bypassAuthoriserEntity')} search={personSearch} value={authoriser?.label ?? null} onSelect={(row) => setAuthoriser({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'employee' }, (row) => setAuthoriser({ id: row.id, label: row.line1 }))} />
            </div>
          ) : null}
          <ModalField label={t('creditNote.form.number')}>
            <span data-credit-note-field="number">{t('creditNote.form.numberOnSave')}</span>
          </ModalField>
          <p className={styles.hint}>{t('creditNote.issuedAtRaising')}</p>
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="creditNote" />
        </>
      }
      lines={
        <>
        <ul className={lineStyles.lineList} data-credit-note-lines={answer?.lines.length ?? 0}>
          {(answer?.lines ?? []).map((row) => {
            const remaining = remainderOf(row);
            const currencyCode = answer?.currencyCode ?? 'USD';
            const discount = lineDiscountOf(row.discountType, row.discountValue, currencyCode);
            // 🔨 The amount rounds once and honours the line's discount, because that is the credit the server will raise; raw multiplication rendered "$4.99.5" on a half quantity.
            const amount = computeLineAmount(quantities[row.sourceLineId] ?? 0, cents(row.unitPriceCents ?? 0), discount ?? undefined).amount;
            return (
              <li key={row.sourceLineId} className={lineStyles.row} data-credit-note-line={row.sourceLineId} data-credit-note-remaining={remaining}>
                <span className={lineStyles.lineMain}>
                  <span className={lineStyles.rowName}>{row.description}</span>
                  <span className={lineStyles.rowNote}>{lineNote(row, quantities[row.sourceLineId] ?? 0)}</span>
                </span>
                <span className={lineStyles.rowMiddle}>
                  <NumericInput size={Size.Small}
                    label={t('creditNote.lines.credit')}
                    labelHidden
                    value={quantities[row.sourceLineId] ?? null}
                    // 🔨 The box is bounded at the remainder rather than defaulted to it: a filled box is a line the save credits, and defaulting would credit every line nobody touched.
                    onChange={(next) => setQuantities((held) => ({ ...held, [row.sourceLineId]: Math.min(Math.max(0, next ?? 0), remaining) }))}
                    kind="number"
                    decimals={2}
                    disabled={remaining <= 0}
                    name={`credit-${row.sourceLineId}`}
                  />
                </span>
                <span className={lineStyles.rowMiddle}>{row.unitPriceCents === null ? '' : <Money cents={row.unitPriceCents} currencyCode={currencyCode} />}</span>
                <span className={lineStyles.rowAmount}><Money cents={amount} currencyCode={currencyCode} /></span>
              </li>
            );
          })}
        </ul>
        {/* 🔒 The one totals box, the same one the Item Table's footer draws, since this line block is not the Item Table (decision 0644). */}
        {answer ? <DocumentTotals answer={preview.answer} failure={preview.failure} fallbackSubtotalCents={creditedNetCents} currencyCode={answer.currencyCode} /> : null}
        </>
      }
    />
    {addNew.host}
    </>
  );
}
